import * as React from 'react';

import { routes } from '@routes/routes.models';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { getEnvConfigs } from '@config/config.services';
import { useAppConfigs, useHasPermission, useProjectIsArchived } from '@config/config.hooks';

import {
  Icon,
  NavItem,
  HorizontalLayoutSingleLevel,
  Grid,
  ToastProvider,
  useIsLoggedIn,
  UnsavedChangesPrompt,
  ComponentType,
  Badge,
  Heading,
  HeadingType,
} from '@gcl/react-core';
import './Layout.scss';
import { T1Icon } from '@common/icons/T1Icon';
import { T2Icon } from '@common/icons/T2Icon';
import { T3Icon } from '@common/icons/T3Icon';
import { T4Icon } from '@common/icons/T4Icon';
import { DTIcon } from '@common/icons/DTIcon';
import { STIcon } from '@common/icons/STIcon';
import { CTIcon } from '@common/icons/CTIcon';
import { useNavigate } from '@routes/routes.hooks';
import usePrompt from '@common/prompt.hooks';
import { LIRIcon } from '@common/icons/LIRIcon';
import { useBreakPoints } from '@common/breakpoints.hooks';
import { DashboardIcon } from '@common/icons/DashboardIcon';
import { hasPermissionInArray } from '@userProfile/permission/permission.services';
import { EPermission, ERolePermission } from '@userProfile/permission/permission.constants';
import { adminPageList } from '@admin/admin.constants';
import { useReportList } from '@report/report.hooks';
import { TransmittalIcon } from '@common/icons/TransmittalIcon';
import { TopNavBar } from './TopNavBar';
import { AnnouncementUnreadBadge } from './AnnouncementUnreadBadge';

export interface ILayoutProps {
  errors?: any;
}

export const Layout: React.FunctionComponent<ILayoutProps> = () => {
  const { navigate, generateRoutePath } = useNavigate();
  const { isXs } = useBreakPoints();

  const appConfigs = useAppConfigs();
  const isProjectArchived = useProjectIsArchived();
  const { pathname } = useLocation();
  const isLoggedInHookValue = useIsLoggedIn();
  const envConfigs = getEnvConfigs();
  const isWebMaster = useHasPermission(EPermission.WEB_MASTER);
  const reportList = useReportList();
  const projectNoFromUrl = appConfigs?.configs.projectNoFromUrl;

  const announcementUnreadBadge = <AnnouncementUnreadBadge />;

  const navItems = React.useMemo(() => {
    let tempNavItems: NavItem<typeof NavLink>[] = [
      // {
      //   icon: <DashboardIcon size={24} />,
      //   key: 'Dashboard',
      //   to: routes.dashboard.path,
      //   text: <span>Dashboard</span>,
      // },
    ];

    if (
      projectNoFromUrl &&
      appConfigs.configs.project?.projectNo &&
      (appConfigs.configs.myProjectUserProfile ||
        hasPermissionInArray(appConfigs.configs.myPermissions ?? [], ERolePermission.VIEWER) === true) &&
      !appConfigs.configs.myProjectUserProfile?.isLocked
    ) {
      tempNavItems = tempNavItems.concat([
        {
          icon: <T1Icon size={24} />,
          key: 'Master Schedule',
          to: generateRoutePath(routes.t1.path),
          text: <span>Master Schedule</span>,
        },
        {
          icon: <T2Icon size={24} />,
          key: 'Design Brief',
          to: generateRoutePath(routes.t2.path),
          text: <span>Design Brief</span>,
        },
        {
          icon: <TransmittalIcon size={20} />,
          text: <span>Transmittal</span>,
          key: 'Transmittal',
          to: generateRoutePath(routes.transmittals.list.path),
          subItems: [
            {
              icon: <DTIcon size={20} />,
              key: 'Designer Transmittal',
              to: generateRoutePath(routes.transmittals.designerTransmittal.path),
              text: <span>Designer Transmittal</span>,
            },
            {
              icon: <STIcon size={20} />,
              key: 'Site Transmittal',
              to: generateRoutePath(routes.transmittals.siteTransmittal.path),
              text: <span>Site Transmittal</span>,
            },
            {
              icon: <CTIcon size={20} />,
              key: 'Checker Transmittal',
              to: generateRoutePath(routes.transmittals.checkerTransmittal.path),
              text: <span>Checker Transmittal</span>,
            },
            ...(envConfigs.enableLir
              ? [
                  {
                    icon: <LIRIcon size={20} />,
                    key: 'Lambeth Independent Review Transmittal',
                    to: generateRoutePath(routes.transmittals.lirTransmittal.path),
                    text: <span>Lambeth Independent Review</span>,
                  },
                ]
              : []),
          ],
        },
        {
          icon: <T3Icon size={24} />,
          key: 'Design Modification',
          to: generateRoutePath(routes.t3.path),
          text: <span>Design Modification</span>,
        },
        {
          icon: <T4Icon size={24} />,
          key: 'Permit to Load',
          to: generateRoutePath(routes.t4.path),
          text: <span>Permit to Load / Remove</span>,
        },
        {
          icon: <Icon iconName="FileEarmark" size={24} />,
          key: 'Document Register',
          to: generateRoutePath(routes.documentRegister.path),
          text: <span>Document Register</span>,
        },
        // {
        //   icon: <Icon iconName="Gear" size={24} />,
        //   key: 'Maintenance',
        //   to: routes.maintenance.path,
        //   text: <span>Maintenance</span>,
        // },
        {
          icon: <Icon iconName="People" size={24} />,
          key: 'Users',
          to: generateRoutePath(routes.users.path),
          text: <span>Users</span>,
        },
        {
          icon: <Icon iconName="InfoCircle" size={24} />,
          key: 'Info',
          to: generateRoutePath(routes.projectInfo.view.path, { id: appConfigs.configs.project.id }),
          text: <span>Project Info</span>,
        },
      ]);
    }

    if (isXs) {
      tempNavItems = tempNavItems.concat([
        {
          icon: <Icon iconName="Megaphone" size={24} />,
          key: 'Megaphone',
          to: routes.announcement.list.path,
          text: <span>{announcementUnreadBadge} Announcement</span>,
        },
        {
          icon: <DashboardIcon size={24} />,
          key: 'Change Project',
          to: routes.projectList.path,
          text: <span>Change Project</span>,
        },
        {
          icon: <Icon iconName="People" size={24} />,
          key: 'MyGems',
          to: generateRoutePath(routes.myGems.path),
          text: <span>My Gems</span>,
        },
      ]);
      if (reportList.length > 0)
        tempNavItems.push({
          icon: <Icon iconName="Clipboard2Data" size={24} />,
          key: 'Reports',
          to: routes.report.main.path,
          text: <span>Reports</span>,
          subItems: reportList.map((r) => {
            return {
              icon: <Icon iconName="Activity" size={18} />,
              key: r.type,
              text: <span>{r.name}</span>,
              to: r.route,
            };
          }),
        });
      if (isWebMaster) {
        const adminListNavItems = adminPageList.map((adminItem) => {
          return {
            icon: <Icon iconName="People" size={18} />,
            key: adminItem.pageType,
            text: adminItem.pageName,
            to: generateRoutePath(routes.admin.item.path, { type: adminItem.pageType }, false),
          };
        });
        tempNavItems.push({
          icon: <Icon iconName="Sliders" size={24} />,
          key: 'Admin',
          to: routes.admin.main.path,
          text: <span>Admin</span>,
          subItems: adminListNavItems,
        });
      }
    }
    return tempNavItems;
  }, [
    JSON.stringify(appConfigs.configs.project),
    JSON.stringify(appConfigs.configs.myProjectUserProfile),
    isXs,
    isWebMaster,
    JSON.stringify(reportList),
    projectNoFromUrl,
    announcementUnreadBadge,
  ]);

  if (!isLoggedInHookValue) return <Outlet />;

  const xsProps = isXs
    ? {
        collapsableNavTopContent: () => (
          <Grid container className="gcl-gems-layout-collapsable-nav-top-content">
            <Grid
              onClick={() => {
                window.open('https://mobile-app.gammonconstruction.com/AppStore/#/appStore', '_blank');
              }}
              container
              item
              alignItems="center"
              justifyContent="flex-start"
              className="gcl-layout-collapsablenav-item  "
            >
              <div className="gcl-gems-layout-collapsable-nav-top-content-item-icon">
                <Icon iconName="Phone" size={24} />
              </div>
              Mobile App
            </Grid>
          </Grid>
        ),
        navBottomContent: (isCollapsed: boolean, closeNav?: () => void) => {
          return <TopNavBar closeNav={closeNav} />;
        },
      }
    : {};

  const content = (
    <>
      {!isXs && <TopNavBar />}
      <Outlet />
    </>
  );

  const projectPage = (
    <HorizontalLayoutSingleLevel
      width="260px"
      expandIconStyle={{ top: '10px' }}
      NavLink={NavLink}
      pathname={pathname}
      navTopContent={(isCollapsed) => (
        <Grid
          sx={[{ flexDirection: { xs: 'row', sm: 'column' } }, !isCollapsed && { padding: '20px' }]}
          container
          spacing={1}
          justifyContent="center"
          alignItems="center"
        >
          <>
            {isXs && (
              <Grid
                item
                alignSelf="flex-start"
                flexDirection={isCollapsed ? 'column' : 'row'}
                sx={[isCollapsed && { textAlign: 'center' }]}
              >
                <img
                  alt="gammon-logo"
                  src="https://logo.gammonconstruction.com/Icon_R.png"
                  className={isCollapsed ? 'gcl-my-md' : 'gcl-mr-sm'}
                />
                <img alt="gems-logo" src="/images/gems-logo.png" width={isCollapsed ? '50px' : 'auto'} />
              </Grid>
            )}
            <Grid
              alignSelf="flex-start"
              item
              sx={[!isCollapsed && { padding: '0 20px' }, isCollapsed && { alignSelf: 'center' }]}
            >
              <Heading as={HeadingType.h2} styledAs={HeadingType.h6}>
                {`${appConfigs.configs.project?.projectNo ?? ''}${
                  !isCollapsed ? ` ${appConfigs.configs.project?.projectName ?? ''}` : ''
                }`}
              </Heading>
              {isProjectArchived && (
                <Badge pill type={ComponentType.Secondary} className="gcl-ml-sm gcl-fb-md gcl-fc-greyscale-100 ">
                  Archived
                </Badge>
              )}
            </Grid>
          </>
        </Grid>
      )}
      {...xsProps}
      navItems={navItems}
    >
      {content}
    </HorizontalLayoutSingleLevel>
  );

  return (
    <div className={`gcl-gems-layout-container `}>
      <span id="account-username" className="gcl-gems-layout-container-username">
        {appConfigs?.configs?.myUserProfile?.email ?? ''}
      </span>
      <UnsavedChangesPrompt
        shouldBlockUIPredicate={() => {
          return appConfigs.configs.isAnyActionLoading ?? false;
        }}
        message="There are actions still loading, if you navigate to another page, this might cause data lost, do you still want to continue?"
        usePrompt={usePrompt}
      />
      <ToastProvider />

      {projectNoFromUrl || isXs ? projectPage : <div className="gcl-gems-layout-global-page-container">{content}</div>}
    </div>
  );
};
